import { format } from 'date-fns';
import { API } from '../api';
import { IFetchPageQuery } from '../IFetchQueryOptions';
import { IPagination } from '../IPagination';
import { INomination } from './interface';
import { DATE_FORMAT_CSV } from '../commonConst';
import { exportCSVData } from '../common/exportCSVData';

export const fetchNominations = async (
    { pagination, sorter, filter, search, company_id = null, include }: IFetchPageQuery,
    signal?: AbortSignal | null
): Promise<IPagination<INomination>> => {
    const limit = pagination?.pageSize && pagination?.pageSize > 0 ? pagination.pageSize : 10;
    const offset = pagination?.current && pagination?.current > 0 ? (pagination.current - 1) * limit : 0;

    const path = `/nominations?${new URLSearchParams({
        offset: offset.toString(),
        limit: limit.toString(),
        ...prepareNominationsOrder(sorter),
        ...prepareNominationsFilter(filter),
        ...(search && { search }),
        ...(company_id && { companyId: company_id }),
        ...(include && { include }),
    })}`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const respBody: any = await response.json();

    if (response.ok) {
        return respBody;
    }

    throw new Error(respBody?.error?.message || `Cannot load list of sites!`);
};

function prepareNominationsOrder(sorter: IFetchPageQuery['sorter']) {
    if (!sorter || !sorter.field || !sorter.order) {
        return {};
    }

    return { sortField: sorter.field, sortOrder: sorter.order };
}

function prepareNominationsFilter(siteFilter: IFetchPageQuery['filter']): IFetchPageQuery['filter'] {
    const filter = { ...siteFilter };

    if (filter?.site_id) {
        filter.siteIds = filter.site_id;
        delete filter.site_id;
    }

    if (filter?.enrollment_id) {
        filter.enrollmentIds = filter.enrollment_id;
        delete filter.enrollment_id;
    }

    if (filter?.utility_id) {
        filter.utilityId = filter.utility_id;
        delete filter.utility_id;
    }

    if (filter?.account_manager) {
        filter.accountManager = filter.account_manager;
        delete filter.account_manager;
    }

    if (filter?.lmp_market) {
        filter.market = filter.lmp_market;
        delete filter.lmp_market;
    }

    if (filter?.control_provider_name) {
        filter.controlProviderId = filter.control_provider_name;
        delete filter.control_provider_name;
    }

    if (filter?.program_name) {
        filter.programId = filter.program_name;
        delete filter.program_name;
    }

    if (filter?.enrollment_status) {
        filter.enrollmentStatus = filter.enrollment_status;
        delete filter.enrollment_status;
    }

    if (filter?.nomination_recency) {
        filter.nominationRecency = filter.nomination_recency;
        delete filter.nomination_recency;
    }

    if (filter?.nomination_status) {
        filter.nominationStatus = filter.nomination_status;
        delete filter.nomination_status;
    }

    return filter;
}

export const exportNominations = async ({ filter, search, include, company_id = null, fileColumnsPreset, fields }: IFetchPageQuery) => {
    const query = new URLSearchParams({
        format: 'csv',
        ...prepareNominationsFilter(filter),
        ...(search && { search }),
        ...(company_id && { companyId: company_id }),
        ...(fileColumnsPreset && { fileColumnsPreset }),
        ...(fields && { fields }),
        ...(include && { include }),
    });

    const response = await API.fetch(`/nominations?${query}`, {
        headers: {
            'Content-Type': 'text/csv',
        },
    });

    const respBody: string = await response.text();

    if (response.ok) {
        const fileName = `nominations--${format(new Date(), DATE_FORMAT_CSV)}.csv`;
        exportCSVData(respBody, fileName);
    } else {
        const respBody: any = await response.json();
        throw new Error(respBody?.error?.message || `Cannot export data!`);
    }
};
